import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  MessageCircle, Send, HelpCircle, Lightbulb, AlertCircle, 
  Heart, X, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminFeedbackButton - Floating button for users to message Admin team
 * 
 * Features:
 * - Floating action button in corner
 * - Message types: Suggestion, Help Request, Bug Report, General
 * - Sends to admin notification email
 */

const MESSAGE_TYPES = [
  { 
    id: 'suggestion', 
    label: 'Suggestion', 
    icon: Lightbulb, 
    color: 'amber',
    placeholder: 'I have an idea to make this better...'
  },
  { 
    id: 'help', 
    label: 'Need Help', 
    icon: HelpCircle, 
    color: 'blue',
    placeholder: 'I need help with...'
  },
  { 
    id: 'bug', 
    label: 'Report Issue', 
    icon: AlertCircle, 
    color: 'red',
    placeholder: 'I found a problem with...'
  },
  { 
    id: 'gratitude', 
    label: 'Share Gratitude', 
    icon: Heart, 
    color: 'emerald',
    placeholder: 'I want to express gratitude for...'
  },
];

const AdminFeedbackButton = ({ 
  position = 'bottom-right',
  userEmail = '',
  userName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messageType, setMessageType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(userEmail);
  const [isSending, setIsSending] = useState(false);

  const currentType = MESSAGE_TYPES.find(t => t.id === messageType);
  const Icon = currentType?.icon || MessageCircle;

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: messageType,
          message: message.trim(),
          email: email.trim() || 'anonymous',
          userName: userName || 'Anonymous User',
          timestamp: new Date().toISOString(),
          page: window.location.pathname,
        }),
      });

      if (response.ok) {
        toast.success('Message sent to the team');
        setMessage('');
        setIsOpen(false);
        setIsExpanded(false);
      } else {
        // Fallback: store locally and show success anyway
        const feedbackStore = JSON.parse(localStorage.getItem('elchme_feedback') || '[]');
        feedbackStore.push({
          type: messageType,
          message: message.trim(),
          email: email.trim(),
          timestamp: new Date().toISOString(),
        });
        localStorage.setItem('elchme_feedback', JSON.stringify(feedbackStore));
        toast.success('Message recorded. Thank you for your feedback!');
        setMessage('');
        setIsOpen(false);
        setIsExpanded(false);
      }
    } catch (error) {
      // Store locally on error
      const feedbackStore = JSON.parse(localStorage.getItem('elchme_feedback') || '[]');
      feedbackStore.push({
        type: messageType,
        message: message.trim(),
        email: email.trim(),
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('elchme_feedback', JSON.stringify(feedbackStore));
      toast.success('Message saved. Thank you!');
      setMessage('');
      setIsOpen(false);
      setIsExpanded(false);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div 
        className={`fixed ${positionClasses[position]} z-50`}
        data-testid="admin-feedback-fab"
      >
        {/* Expanded quick options */}
        {isExpanded && !isOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-2 mb-2">
            {MESSAGE_TYPES.map((type) => {
              const TypeIcon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    setMessageType(type.id);
                    setIsOpen(true);
                    setIsExpanded(false);
                  }}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-zinc-800/90 backdrop-blur-sm border border-zinc-700/50
                    text-sm text-zinc-300 hover:text-white
                    hover:border-${type.color}-500/50 hover:bg-zinc-700/90
                    transition-all duration-200 shadow-lg
                    animate-in slide-in-from-bottom-2
                  `}
                  style={{ animationDelay: `${MESSAGE_TYPES.indexOf(type) * 50}ms` }}
                  data-testid={`feedback-type-${type.id}`}
                >
                  <TypeIcon className={`w-4 h-4 text-${type.color}-400`} />
                  {type.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Main FAB */}
        <button
          onClick={() => {
            if (isOpen) {
              setIsOpen(false);
            } else if (isExpanded) {
              setIsExpanded(false);
            } else {
              setIsExpanded(true);
            }
          }}
          className={`
            w-10 h-10 rounded-full
            bg-gradient-to-br from-amber-500 to-amber-600
            hover:from-amber-400 hover:to-amber-500
            text-black shadow-lg shadow-amber-500/20
            flex items-center justify-center
            transition-all duration-300
            ${isExpanded ? 'rotate-45' : ''}
          `}
          data-testid="feedback-fab-button"
        >
          {isExpanded ? (
            <X className="w-4 h-4" />
          ) : (
            <MessageCircle className="w-4 h-4" />
          )}
        </button>

        {/* Tiny label */}
        {!isExpanded && !isOpen && (
          <span className="absolute -top-1 -left-1 text-[8px] bg-amber-500 text-black px-1 rounded-full font-medium">
            Help
          </span>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent 
          className="bg-zinc-900/95 backdrop-blur-xl border-zinc-700/50 max-w-md"
          data-testid="admin-feedback-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Icon className={`w-5 h-5 text-${currentType?.color}-400`} />
              {currentType?.label}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Message Type Selector */}
            <div className="flex gap-2">
              {MESSAGE_TYPES.map((type) => {
                const TypeIcon = type.icon;
                const isActive = messageType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => setMessageType(type.id)}
                    className={`
                      flex-1 p-2 rounded-lg border transition-all
                      ${isActive 
                        ? `bg-${type.color}-500/20 border-${type.color}-500/50 text-${type.color}-300` 
                        : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-500 hover:text-zinc-300'
                      }
                    `}
                    title={type.label}
                  >
                    <TypeIcon className="w-4 h-4 mx-auto" />
                  </button>
                );
              })}
            </div>

            {/* Email (optional) */}
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">
                Your email (optional, for follow-up)
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-600"
              />
            </div>

            {/* Message */}
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">
                Your message
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={currentType?.placeholder}
                className="min-h-[120px] bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="feedback-message-input"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={isSending || !message.trim()}
              className="w-full bg-amber-600 hover:bg-amber-500 text-black"
              data-testid="feedback-submit-btn"
            >
              {isSending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to Team
                </>
              )}
            </Button>

            <p className="text-xs text-zinc-600 text-center">
              Your feedback helps us build a better experience for everyone.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminFeedbackButton;
